import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, EyeOff, Plus, Repeat, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { DatePicker } from "@/components/common/DatePicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CAT_COLORS } from "@/components/finance/BudgetOverview";
import { EXPENSE_CATS } from "@/components/finance/ExpensesCard";
import { api, formatApiError } from "@/lib/api";
import { OffersPanel, QuoteAction, SavingHighlight } from "@/components/finance/SavingActions";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

export const FREQ_LABELS = { monthly: "Mensuel", quarterly: "Trimestriel", yearly: "Annuel" };
const REASON_TONE = { duplicate: "bg-destructive/10 text-destructive", increase: "bg-butter/40", market: "bg-secondary", cancel: "bg-destructive/10 text-destructive" };

export const useRecurring = (scope, householdId) => useQuery({
  queryKey: ["recurring", scope, householdId],
  queryFn: () => api.get("/finance/recurring", { params: { scope, household_id: householdId } }).then((r) => r.data),
});

const AddForm = ({ ctx, onDone }) => {
  const [v, setV] = useState({ name: "", amount: "", frequency: "monthly", category: "abonnements", next_date: "" });
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/recurring", { ...ctx, ...v, amount: Number(v.amount), next_date: v.next_date || null });
      toast.success("Charge récurrente ajoutée");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="grid gap-2 rounded-xl border border-dashed border-border p-3 sm:grid-cols-2 lg:grid-cols-[1fr_7rem_9rem_10rem_11rem_auto]" data-testid="recurring-add-form">
      <Input value={v.name} onChange={set("name")} placeholder="Ex. Spotify, salle de sport" required minLength={2} data-testid="recurring-input-name" />
      <Input type="number" min="0.01" step="0.01" value={v.amount} onChange={set("amount")} placeholder="Montant" required data-testid="recurring-input-amount" />
      <Select value={v.frequency} onValueChange={set("frequency")}><SelectTrigger data-testid="recurring-input-frequency"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(FREQ_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      <Select value={v.category} onValueChange={set("category")}><SelectTrigger data-testid="recurring-input-category"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(EXPENSE_CATS).filter(([k]) => k !== "banque").map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      <DatePicker value={v.next_date} onChange={set("next_date")} placeholder="Prochain prélèvement" data-testid="recurring-input-next-date" />
      <Button type="submit" className="rounded-full" data-testid="recurring-add-submit">Ajouter</Button>
    </form>
  );
};

const Actions = ({ it, ctx, onChange }) => {
  const put = async (body) => { try { await api.put(`/finance/recurring/${it.key}`, { ...ctx, ...body }); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async () => { try { await api.delete(`/finance/recurring/${it.key}`, { params: ctx }); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  if (it.status === "ignored") return <Button size="sm" variant="ghost" className="rounded-full" onClick={() => put({ status: "detected" })} data-testid={`recurring-restore-${it.key}`}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Rétablir</Button>;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Select value={it.flag || "none"} onValueChange={(f) => put({ flag: f })}>
        <SelectTrigger className="h-8 w-36 rounded-full text-xs" data-testid={`recurring-flag-${it.key}`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="none">Aucune action</SelectItem><SelectItem value="cancel">À résilier</SelectItem><SelectItem value="renegotiate">À renégocier</SelectItem></SelectContent>
      </Select>
      <QuoteAction it={it} base={`/finance/recurring/${it.key}`} params={ctx} onChange={onChange} />
      {it.source === "detected" && it.status !== "confirmed" && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => put({ status: "confirmed" })} aria-label="Confirmer" data-testid={`recurring-confirm-${it.key}`}><Check className="h-4 w-4" /></Button>}
      {it.source === "detected" && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => put({ status: "ignored" })} aria-label="Ignorer" data-testid={`recurring-ignore-${it.key}`}><EyeOff className="h-4 w-4" /></Button>}
      {it.source === "manual" && <ConfirmDelete title="Supprimer cette charge ?" text={`« ${it.name} » sera retirée.`} testid={`recurring-delete-${it.key}`} onConfirm={del} />}
    </div>
  );
};

const Row = ({ it, ctx, onChange }) => {
  const { money, date } = useFormat();
  const m = (v) => money(v, { maximumFractionDigits: 2 });
  return (
    <li className={cn("rounded-xl border border-border bg-card/70 p-4 text-sm", it.status === "ignored" && "opacity-60")} data-testid={`recurring-item-${it.key}`}>
      <div className="flex flex-wrap items-start gap-3">
        <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: CAT_COLORS[it.category] }} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><span className="font-semibold">{it.name}</span>
            <Badge variant="outline" className="rounded-full text-[10px]">{FREQ_LABELS[it.frequency]}</Badge>
            {it.status === "confirmed" && <Badge className="rounded-full bg-sage/20 text-[10px] text-foreground hover:bg-sage/20" data-testid={`recurring-confirmed-${it.key}`}>Confirmé</Badge>}
            {it.source === "manual" && <Badge variant="secondary" className="rounded-full text-[10px]">Ajouté à la main</Badge>}
            {it.flag && <Badge variant="secondary" className="rounded-full text-[10px]">{it.flag === "cancel" ? "À résilier" : "À renégocier"}</Badge>}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">{it.type_label || it.category_label} · {m(it.amount)} / prélèvement{it.next_date && ` · prochain le ${date(it.next_date)}`}</div>
        </div>
        <div className="text-right"><div className="num font-bold" data-testid={`recurring-monthly-${it.key}`}>{m(it.monthly_cost)}<span className="text-xs font-normal text-muted-foreground"> /mois</span></div>
          <div className="num text-xs text-muted-foreground">{money(it.annual_cost, { maximumFractionDigits: 0 })} /an</div></div>
      </div>
      <SavingHighlight it={it} testid={`recurring-saving-${it.key}`} />
      <OffersPanel it={it} base={`/finance/recurring/${it.key}`} params={ctx} onChange={onChange} />
      {it.status !== "ignored" && it.savings.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`recurring-savings-${it.key}`}>
          {it.savings.map((r) => <span key={r.reason} className={cn("rounded-full px-2.5 py-1 text-xs", REASON_TONE[r.reason])}>{r.text} · <span className="num font-semibold">−{m(r.amount)}/mois</span></span>)}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{it.flag === "renegotiate" ? "À renégocier : comparez les offres" : ""}</span>
        <Actions it={it} ctx={ctx} onChange={onChange} />
      </div>
    </li>
  );
};

const Totals = ({ t }) => {
  const { money } = useFormat();
  const cells = [["Charges récurrentes", `${t.count}`, "count"], ["Coût mensuel", money(t.monthly, { maximumFractionDigits: 0 }), "monthly"],
    ["Coût annuel", money(t.annual, { maximumFractionDigits: 0 }), "annual"], ["Économie possible", `${money(t.savings_monthly, { maximumFractionDigits: 0 })}/mois`, "savings"]];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cells.map(([l, v, k]) => (
        <div key={k} className={cn("rounded-2xl border border-border bg-card/70 p-4", k === "savings" && "saving-strong")} data-testid={`recurring-total-${k}`}>
          <div className="text-xs text-muted-foreground">{l}</div><div className="num mt-1 text-xl font-extrabold sm:text-2xl">{v}</div>
          {k === "savings" && <div className="text-xs text-muted-foreground">soit {money(t.savings_annual, { maximumFractionDigits: 0 })} par an</div>}
          {k === "savings" && t.realised_monthly > 0 && <div className="mt-1 text-xs font-semibold text-success" data-testid="recurring-total-realised">Déjà obtenu : −{money(t.realised_monthly, { maximumFractionDigits: 0 })}/mois</div>}
        </div>
      ))}
    </div>
  );
};

export const RecurringCard = ({ scope, householdId }) => {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [showIgnored, setShowIgnored] = useState(false);
  const { data, isLoading } = useRecurring(scope, householdId);
  const ctx = { scope, household_id: householdId };
  const refresh = () => qc.invalidateQueries({ queryKey: ["recurring"] });
  const items = data?.items || [];
  const ignored = items.filter((i) => i.status === "ignored");
  useEffect(() => {
    if (data && scope === "personal" && window.location.hash === "#recurring") {
      const go = () => document.getElementById("recurring")?.scrollIntoView({ behavior: "smooth" });
      const ids = [100, 900, 1800].map((ms) => setTimeout(go, ms));
      return () => ids.forEach(clearTimeout);
    }
  }, [data, scope]);
  return (
    <section id="recurring" className="card-surface tone-wash tone-peach scroll-mt-24 space-y-5 p-5 sm:p-6" data-testid={`recurring-card-${scope}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="tone-chip flex h-9 w-9 items-center justify-center rounded-xl"><Repeat className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1"><h2 className="text-lg font-bold">Charges récurrentes</h2>
          <p className="text-xs text-muted-foreground">Abonnements et prélèvements détectés par fournisseur. Économie = doublons + hausses de prix + repères marché indicatifs + charges à résilier.</p></div>
        <Button variant="outline" className="w-full rounded-full sm:w-auto" onClick={() => setAdding(!adding)} data-testid="recurring-add-button"><Plus className="mr-1 h-4 w-4" /> Ajouter une charge</Button>
      </div>
      {adding && <AddForm ctx={ctx} onDone={() => { setAdding(false); refresh(); }} />}
      {isLoading || !data ? <div className="h-32 animate-pulse rounded-2xl bg-muted" /> : (
        <>
          <Totals t={data.totals} />
          {items.length - ignored.length ? (
            <ul className="stagger space-y-3" data-testid={`recurring-list-${scope}`}>{items.filter((i) => i.status !== "ignored").map((it) => <Row key={it.key} it={it} ctx={ctx} onChange={refresh} />)}</ul>
          ) : <p className="text-sm text-muted-foreground" data-testid="recurring-empty">Aucune charge récurrente détectée pour l'instant (il faut au moins deux prélèvements).</p>}
          {ignored.length > 0 && (
            <div>
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setShowIgnored(!showIgnored)} data-testid="recurring-toggle-ignored"><ChevronDown className={cn("mr-1 h-4 w-4 transition-transform", showIgnored && "rotate-180")} /> Ignorées ({ignored.length})</Button>
              {showIgnored && <ul className="mt-2 space-y-3">{ignored.map((it) => <Row key={it.key} it={it} ctx={ctx} onChange={refresh} />)}</ul>}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">Repères marché indicatifs CASHMY (offres d'entrée de gamme courantes : box 25 €, mobile 10 €, streaming 9 €, musique 11 € par mois), à vérifier avant de changer d'offre.</p>
        </>
      )}
    </section>
  );
};
